import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, Lightbulb, BookOpen, Droplets, ArrowRight, RefreshCw, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import tips, { getDailyTip } from "@/data/tipsData";

const BeautyHubHomeSection = ({ onOpenQuiz }) => {
  const { language } = useLanguage();
  const isFr = language === "fr";

  const [tipIndex, setTipIndex] = useState(() => getDailyTip(tips));

  const nextTip = () => setTipIndex((prev) => (prev + 1) % tips.length);

  const blogPreviews = [
    { en: "Botox Areas Explained: What Each Zone Treats", fr: "Zones Botox Expliquées: Ce Que Chaque Zone Traite", tag: "botox", min: 4 },
    { en: "Baby Glow + Botox: The Perfect Pair", fr: "Baby Glow + Botox: Le Duo Parfait", tag: "combos", min: 5 },
    { en: "Sculptra vs Fillers: Which Is Right for You?", fr: "Sculptra vs Fillers: Lequel est fait pour vous?", tag: "treatments", min: 6 },
  ];

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-b from-[#faf8f5] to-white relative overflow-hidden" id="aesthetic-guide" data-testid="aesthetic-guide-home-section">
      {/* Background texture */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000' fill-opacity='1'%3E%3Cpath d='M20 20c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2z'/%3E%3C/g%3E%3C/svg%3E")`,
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        {/* Section Header */}
        <div className="text-center mb-12">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-amber-700 text-xs tracking-[0.25em] uppercase font-medium mb-3"
          >
            {isFr ? "CONSEILS D'EXPERTE EN ESTHÉTIQUE" : "EXPERT AESTHETIC INSIGHTS"}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl text-zinc-900 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {isFr ? "Guide " : "Karine's "}
            <span className="italic text-amber-700">{isFr ? "Demandez Karine FAQ" : "Ask Karine FAQ"}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-gray-500 text-sm sm:text-base max-w-xl mx-auto"
          >
            {isFr
              ? "Botox, fillers, Sculptra, Baby Glow — conseils d'experte et guides de traitement par Karine"
              : "Botox, fillers, Sculptra, Baby Glow — expert advice and treatment guides from Karine"}
          </motion.p>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* LEFT: Daily Tip + Quiz CTA */}
          <div className="lg:col-span-1 flex flex-col gap-6">
            {/* Daily Tip Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl border border-amber-100 shadow-sm overflow-hidden flex-1"
              data-testid="daily-tip-card"
            >
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <Lightbulb className="w-4 h-4 text-amber-600" />
                  </div>
                  <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
                    {isFr ? "Conseil du Jour" : "Tip of the Day"}
                  </span>
                </div>
                <button onClick={nextTip} className="p-1.5 rounded-full hover:bg-amber-100 transition-colors" data-testid="next-tip-btn">
                  <RefreshCw className="w-3.5 h-3.5 text-amber-600" />
                </button>
              </div>
              <div className="p-5">
                <p className="text-gray-700 text-sm leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                  "{isFr ? tips[tipIndex].fr : tips[tipIndex].en}"
                </p>
              </div>
              <div className="px-5 pb-4">
                <Link
                  to="/aesthetic-guide?tab=tip"
                  className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1"
                  data-testid="see-all-tips-link"
                >
                  {isFr ? "Voir tous les conseils" : "See all tips"} <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </motion.div>

            {/* Treatment Quiz CTA */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              onClick={onOpenQuiz}
              className="bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 rounded-2xl p-6 text-left group hover:shadow-xl transition-all relative overflow-hidden"
              data-testid="homepage-quiz-cta"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl pointer-events-none" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mb-4 shadow-lg shadow-amber-500/30">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white text-lg font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {isFr ? "Trouvez Votre Traitement" : "Find Your Treatment"}
                </h3>
                <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                  {isFr
                    ? "Répondez à quelques questions et découvrez les traitements parfaits pour vous"
                    : "Answer a few questions and discover the perfect treatments for you"}
                </p>
                <span className="inline-flex items-center gap-2 text-amber-400 text-sm font-medium group-hover:gap-3 transition-all">
                  {isFr ? "Commencer le Quiz" : "Start Quiz"} <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </motion.button>
          </div>

          {/* CENTER: Blog Previews */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-1 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
            data-testid="blog-preview-card"
          >
            <div className="bg-gradient-to-r from-rose-50 to-pink-50 px-5 py-3 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-rose-600" />
              </div>
              <span className="text-xs font-semibold text-rose-800 uppercase tracking-wider">
                {isFr ? "Articles Récents" : "Latest Articles"}
              </span>
            </div>
            <div className="divide-y divide-gray-50">
              {blogPreviews.map((post, idx) => (
                <Link
                  key={idx}
                  to="/aesthetic-guide?tab=blog"
                  className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors group"
                  data-testid={`blog-preview-${idx}`}
                >
                  <div className="flex-1">
                    <span className="inline-block text-[10px] uppercase tracking-wider text-rose-500 font-medium mb-1">{post.tag} · {post.min} min</span>
                    <h4 className="text-sm font-medium text-gray-900 leading-snug group-hover:text-rose-700 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {isFr ? post.fr : post.en}
                    </h4>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-rose-500 transition-colors flex-shrink-0" />
                </Link>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-gray-50">
              <Link
                to="/aesthetic-guide?tab=blog"
                className="text-xs text-rose-600 hover:text-rose-700 font-medium flex items-center gap-1"
                data-testid="view-all-articles-link"
              >
                {isFr ? "Voir tous les articles" : "View all articles"} <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          </motion.div>

          {/* RIGHT: Skincare Quiz + Routine Builder */}
          <div className="lg:col-span-1 flex flex-col gap-6">
            {/* Skincare Quiz Teaser */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-6 border border-violet-100 flex-1"
              data-testid="skincare-quiz-teaser"
            >
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center mb-4">
                <Sparkles className="w-5 h-5 text-violet-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isFr ? "Quel est votre âge de peau?" : "What's Your Skin Age?"}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {isFr
                  ? "Un quiz rapide de 2 minutes pour découvrir l'âge réel de votre peau et des recommandations personnalisées."
                  : "A quick 2-minute quiz to discover your skin's real age and get personalized recommendations."}
              </p>
              <Link
                to="/aesthetic-guide?tab=quiz"
                className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 text-sm font-medium"
                data-testid="take-skin-quiz-link"
              >
                {isFr ? "Faire le Quiz" : "Take the Quiz"} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            {/* Routine Builder CTA */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-6 border border-cyan-100"
              data-testid="routine-builder-teaser"
            >
              <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center mb-4">
                <Droplets className="w-5 h-5 text-cyan-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isFr ? "Ma Routine Personnalisée" : "My Custom Routine"}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {isFr
                  ? "Créez votre routine avec les produits SkinCeuticals recommandés par Karine."
                  : "Build your routine with SkinCeuticals products recommended by Karine."}
              </p>
              <Link
                to="/aesthetic-guide?tab=routine"
                className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 text-sm font-medium"
                data-testid="build-routine-link"
              >
                {isFr ? "Créer Ma Routine" : "Build My Routine"} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center mt-10"
        >
          <Link
            to="/aesthetic-guide"
            className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-8 py-3 rounded-full text-sm font-medium transition-colors shadow-lg shadow-zinc-900/20"
            data-testid="explore-aesthetic-guide-btn"
          >
            {isFr ? "Explorer Demandez Karine FAQ" : "Explore Ask Karine FAQ"} <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default BeautyHubHomeSection;
